/**
 * The desktop masthead's top bar — shown or folded away, one choice per browser.
 *
 * WHAT. On a desktop the masthead is the row of nav pills. The bar above it
 * (the Clavis mark, the studio's logo, the theme switch, the version chip, who
 * is signed in, Your studios, Logout, Sign out everywhere) is folded away by
 * default and opened by the small icon pill after "More"; pressing it again
 * folds it (Mario, 2026-10-24: "This whole row looks very messy to me. Just
 * put a little icon next to 'More'. When it's pressed, this bar automatically
 * appears, and when it's pressed again, all the stuff on top basically
 * disappears").
 *
 * The choice lives in `localStorage["layer.masthead-bar"]` (`"open"` or
 * absent), per browser and per origin, and outlives sign-out like the theme:
 * it is a display preference, not session state. Every access is guarded —
 * `localStorage` throws in some contexts — and a failed read means folded, a
 * failed write means the choice lasts for this page only.
 */

import type { StorageLike } from "./saved-logins";

export const MASTHEAD_BAR_STORAGE_KEY = "layer.masthead-bar";

/** The stored choice; anything absent, unreadable or unknown is folded. */
export function readMastheadBarOpen(storage: StorageLike | null): boolean {
  if (storage === null) return false;
  try {
    return storage.getItem(MASTHEAD_BAR_STORAGE_KEY) === "open";
  } catch {
    return false;
  }
}

/** Remember the choice. Folded is the default, so it is stored as absence. */
export function writeMastheadBarOpen(
  storage: StorageLike | null,
  open: boolean,
): void {
  if (storage === null) return;
  try {
    if (open) storage.setItem(MASTHEAD_BAR_STORAGE_KEY, "open");
    else storage.removeItem(MASTHEAD_BAR_STORAGE_KEY);
  } catch {
    // Blocked storage: the bar still opens and folds, just not remembered.
  }
}
